import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, useCallback, useContext, useMemo, useState } from 'react';
import { isMediumUI } from 'shared/app/lib/choose-ui-type';
import { SettingsDialogWithRouter } from 'console/dialogs/universal';
import { List as MaterialList, ListItem, ListItemText } from '@mui/material';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { SaveApplyButtons } from 'z3k-settings/layout/save-apply-buttons';
import { clear_error_account_dialog, save_ui_settings, stop_loading_account_dialog, update_account, } from 'shared/account.settings/redux/actions';
import { useBox, usePartial } from 'shared/hooks/utils';
import { MessageBox } from 'shared/components/dialogs/message-box';
import { RequestPasswordDialog } from 'shared/account.settings/ui/request-password-dialog';
import { useBooleanState } from 'shared/hooks/state/boolean';
import { useNavigateRoot } from 'shared/hooks/router';
import { AccountEditor } from 'shared/account.settings/model/types';
export var AccountDialogSaveType;
(function (AccountDialogSaveType) {
    AccountDialogSaveType["Save"] = "Save";
    AccountDialogSaveType["Apply"] = "Apply";
})(AccountDialogSaveType || (AccountDialogSaveType = {}));
const LazyUi = lazy(() => import(
// webpackChunkName: 'account-dialog'
'console/widgets/account.settings/ui/layout'));
const select_account_dialog = (state) => state.account_dialog;
const NO_PASSWORD_FIELDS = ['themeType'];
export function AccountSettingsContent() {
    const { initial_account, validation_result, account_diff, ui_settings_diff } = useContext(AccountEditor);
    const boxedDiffAccount = useBox(account_diff);
    const boxedDiffUiSettings = useBox(ui_settings_diff);
    const boxedValidationResult = useBox(validation_result);
    const [saveType, setSaveType] = useState(AccountDialogSaveType.Apply);
    const account_dialog = useAppSelector(select_account_dialog);
    const { loading, error } = account_dialog;
    const is_error = useMemo(() => error != null && error.length > 0, [error]);
    const [invalid_dialog_open, closeInvalidDialog, openInvalidDialog] = useBooleanState(false);
    const [is_password_dialog_open, closePasswordDialog, openPasswordDialog] = useBooleanState(false);
    const closeDialog = useNavigateRoot();
    const onUpdateAccount = useAction(update_account);
    const onSaveUiSettings = useAction(save_ui_settings);
    const stopLoading = useAction(stop_loading_account_dialog);
    const onClearError = useAction(clear_error_account_dialog);
    //@ts-ignore
    const error_list = useMemo(() => (validation_result != null ? Object.values(validation_result) : []), [validation_result]);
    const doUpdateAccount = useCallback(async (diff, current_password) => {
        await onUpdateAccount(diff, current_password, (diff.webui_beta != null &&
            diff.webui_beta !== initial_account.webui_beta) ||
            diff.password != null);
    }, [initial_account.webui_beta]);
    const onStartSave = useCallback(async (save_type) => {
        setSaveType(save_type);
        //если не прошли валидацию
        if (boxedValidationResult.current != null) {
            openInvalidDialog();
            return;
        }
        const accountDiff = boxedDiffAccount.current;
        const uiSettingsDiff = boxedDiffUiSettings.current;
        // если нечего сохранять
        if (Object.keys(accountDiff).length === 0 &&
            Object.keys(uiSettingsDiff).length === 0) {
            // если кнопка сохранить то закрываем диалог
            if (save_type === AccountDialogSaveType.Save) {
                closeDialog();
            }
            return;
        }
        // поля, не требующие пароль для сохранения, записаны в NO_PASSWORD_FIELDS
        // проверим, нужен ли пароль
        let is_password_required = false;
        if (Object.keys(accountDiff).length > 0) {
            is_password_required = true;
        }
        if (!is_password_required) {
            for (const key of Object.keys(uiSettingsDiff)) {
                if (!NO_PASSWORD_FIELDS.includes(key)) {
                    is_password_required = true;
                    break;
                }
            }
        }
        // если есть только поля, сохраняемые без пароля
        if (!is_password_required) {
            // сохранить без ввода пароля
            // только uiSettings, т.к. аккаунт сохраняется только с паролем
            await onSaveUiSettings(uiSettingsDiff);
            stopLoading();
            // если кнопка сохранить то закрываем диалог
            if (save_type === AccountDialogSaveType.Save) {
                closeDialog();
            }
            return;
        }
        // открыть диалог с паролем
        openPasswordDialog();
    }, []);
    const onAcceptPassword = useCallback(async (password) => {
        if (password.trim().length > 0) {
            closePasswordDialog();
            //чтобы проверить пароль, нужно обязательно сходить к update_account
            await doUpdateAccount(boxedDiffAccount.current, password);
            if (Object.keys(boxedDiffUiSettings.current).length) {
                await onSaveUiSettings(boxedDiffUiSettings.current);
            }
            stopLoading();
            if (saveType === AccountDialogSaveType.Save) {
                // без setTimeout closeDialog не срабатывает, если нет задержки при работе api
                setTimeout(() => closeDialog(), 0);
            }
        }
    }, [doUpdateAccount, onSaveUiSettings, closeDialog, saveType]);
    const onSave = usePartial(onStartSave, AccountDialogSaveType.Save);
    const onApply = usePartial(onStartSave, AccountDialogSaveType.Apply);
    const is_medium_ui = isMediumUI();
    return (_jsxs(_Fragment, { children: [_jsx(SettingsDialogWithRouter, { title: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C", fullWidth: true, is_medium_ui: is_medium_ui, maxWidth: "md", content_sx: is_medium_ui ? { display: 'flex', flexFlow: 'row nowrap' } : undefined, contentWithZeroPadding: true, noIPhoneBottomPadding: true, footer: is_medium_ui ? (_jsx(SaveApplyButtons, { loading: loading, onSave: onSave, onApply: onApply })) : null, children: _jsx(LazyUi, { dirty: Object.keys(account_diff).length > 0 ||
                        Object.keys(ui_settings_diff).length > 0, loading: loading, onSave: onSave }) }), _jsx(RequestPasswordDialog, { open: is_password_dialog_open, onClose: closePasswordDialog, onAccept: onAcceptPassword }), _jsx(MessageBox, { title: "\u041F\u043E\u0434\u043E\u0436\u0434\u0438\u0442\u0435", open: loading, isWarning: true, children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u0438\u0435 \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043A..." }), _jsx(MessageBox, { title: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0441\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438", open: is_error, isError: true, onClose: onClearError, children: error }), _jsx(MessageBox, { isError: true, open: invalid_dialog_open, onClose: closeInvalidDialog, title: "\u041D\u0435\u043A\u043E\u0440\u0440\u0435\u043A\u0442\u043D\u044B\u0435 \u043D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438", children: _jsx(MaterialList, { component: "nav", children: error_list.map((error, i) => (_jsx(ListItem, { divider: error_list.length > 1, children: _jsx(ListItemText, { primary: error }) }, i))) }) })] }));
}
